<template>

  <section class="relative overflow-hidden bg-linear-to-br from-pink-50 via-white to-purple-50">
    <div
      class="absolute top-20 left-10 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse">
    </div>
    <div
      class="absolute bottom-10 right-10 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse delay-1000">
    </div>

    <div class="max-w-6xl mx-auto px-6 py-16 md:py-24 relative z-10">
      <div class="grid md:grid-cols-2 gap-12 items-center">

        <div>
          <h1 class="text-5xl md:text-6xl font-extrabold tracking-tight leading-tight">
            <span class="bg-linear-to-r from-pink-600 to-purple-700 bg-clip-text text-transparent neon-glow">Where is
              the</span><br>
            <span class="text-gray-900">
              <TextRotate :interval="1000" :items="EventTypes.map(e => e.label)" /> in {{ city?.name }}?
            </span>
          </h1>
          <p class="text-xl text-gray-700 mt-6 max-w-lg">
            <span class="font-semibold">The last lonely night out.</span> Karaoke nights, powered by the crowd. Discover
            events, find a partner, or just show up to cheer friends.
          </p>

          <div class="mt-8 flex flex-col sm:flex-row gap-4">
            <a href="/events"
              class="bg-gray-900 text-white px-8 py-4 rounded-full text-lg font-semibold shadow-xl hover:bg-gray-800 transition flex items-center justify-center gap-2 card-hover">
              📍 Find All Events
            </a>
            <a href="/start"
              class="bg-white border-2 border-gray-300 text-gray-800 px-8 py-4 rounded-full text-lg font-semibold hover:border-pink-400 hover:text-pink-600 transition flex items-center justify-center">
              Start a Club
            </a>
          </div>

          <p class="mt-6 text-sm text-gray-500 flex items-center gap-2">
            <span class="flex -space-x-2">
              <span
                class="w-7 h-7 rounded-full bg-amber-200 border-2 border-white flex items-center justify-center text-xs">🎤</span>
              <span
                class="w-7 h-7 rounded-full bg-blue-200 border-2 border-white flex items-center justify-center text-xs">🎧</span>
              <span
                class="w-7 h-7 rounded-full bg-green-200 border-2 border-white flex items-center justify-center text-xs">⭐</span>
            </span>
            <span>1.2K people clubbed last night in {{ city?.name }} 🔥</span>
          </p>
        </div>

        <div class="relative flex justify-center">
          <div class="relative w-full max-w-sm">
            <div
              class="absolute inset-0 bg-linear-to-tr from-pink-400 to-purple-500 rounded-3xl rotate-3 scale-105 opacity-20 blur-xl">
            </div>
            <img src="/assets/image-gen.jpg" alt="Crowd cheering at karaoke"
              class="rounded-3xl shadow-2xl border-8 border-white/80 relative z-10 object-cover h-96 w-full">
            <div
              class="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl px-6 py-3 flex items-center gap-2 z-20">
              <div class="inline-block p-0.75 rounded-full bg-linear-to-tr from-yellow-400 via-pink-500 to-purple-600">

                <img src="/assets/mic-tohbet.jpg" class="w-12 h-12 rounded-full object-cover border-2 border-white" />
              </div>
              <blockquote class="font-semibold flex-1">“Sang with strangers who became friends” - Mike Tohbet
              </blockquote>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>


  <section id="how-it-works" class="py-24 bg-white border-t border-gray-100">
    <div class="max-w-6xl mx-auto px-6">
      <h2 class="text-4xl md:text-5xl font-bold text-center tracking-tight">Your journey from the mic to the fame</h2>
      <p class="text-center text-gray-600 text-lg max-w-2xl mx-auto mt-4">From couch to chorus — zero awkwardness.</p>

      <div class="grid md:grid-cols-3 gap-8 mt-16">

        <div class="bg-gray-50 rounded-3xl p-8 card-hover border border-gray-100">
          <span class="text-5xl">🔍</span>
          <h3 class="text-2xl font-bold mt-6 mb-2">Discover</h3>
          <p class="text-gray-600">See live crowd counts & who’s going solo. No more empty rooms — pick the night with
            energy.</p>
          <div class="mt-6 flex items-center text-sm text-pink-600 font-medium">
            <span>real‑time map</span>
            <span class="ml-2">→</span>
          </div>
        </div>

        <div class="bg-gray-50 rounded-3xl p-8 card-hover border border-gray-100">
          <span class="text-5xl">🤝</span>
          <h3 class="text-2xl font-bold mt-6 mb-2">Connect</h3>
          <p class="text-gray-600">Toggle “solo mode” and instantly match with a crew. Arrive together, sing together.
          </p>
          <div class="mt-6 flex items-center text-sm text-pink-600 font-medium">
            <span>crew formation</span>
            <span class="ml-2">→</span>
          </div>
        </div>

        <div class="bg-gray-50 rounded-3xl p-8 card-hover border border-gray-100">
          <span class="text-5xl">✨</span>
          <h3 class="text-2xl font-bold mt-6 mb-2">Shine</h3>
          <p class="text-gray-600">Get cheered by real humans, earn your Wingman score, and own the stage — no
            algorithms.</p>
          <div class="mt-6 flex items-center text-sm text-pink-600 font-medium">
            <span>cheer button</span>
            <span class="ml-2">→</span>
          </div>
        </div>
      </div>
    </div>
  </section>


  <section class="bg-linear-to-r from-indigo-50 to-pink-50 py-20">
    <div class="max-w-5xl mx-auto px-6 text-center">
      <span class="text-7xl mb-4 inline-block">🎤🤔</span>
      <h2 class="text-4xl md:text-5xl font-bold max-w-3xl mx-auto leading-tight">“I really want to sing, but I don’t
        want to go alone.”</h2>
      <p class="text-xl text-gray-700 mt-6 max-w-2xl mx-auto">— Every future rockstar. We turn that fear into fuel.</p>
      <div class="flex flex-wrap justify-center gap-4 mt-10">
        <div class="bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-sm flex items-center gap-2">
          <span class="w-2 h-2 bg-green-400 rounded-full"></span>
          <span class="font-medium">Singer</span>
        </div>
        <div class="bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-sm flex items-center gap-2">
          <span class="w-2 h-2 bg-purple-400 rounded-full"></span>
          <span class="font-medium">Singer</span>
        </div>
        <div class="bg-white/80 backdrop-blur-sm rounded-full px-6 py-3 shadow-sm flex items-center gap-2">
          <span class="w-2 h-2 bg-pink-400 rounded-full"></span>
          <span class="font-medium">Singer</span>
        </div>
        <span class="text-2xl mx-2">+</span>
        <div class="bg-black text-white rounded-full px-6 py-3 shadow-sm flex items-center gap-2">
          <span>🎉 instant crew</span>
        </div>
      </div>
      <p class="text-gray-600 mt-8 text-lg">Arrive as strangers, leave as the crew who watched each other hit that high
        note.</p>
    </div>
  </section>

  <section class="py-16 bg-white border-t border-gray-200">
    <div class="max-w-5xl mx-auto px-6 text-center">
      <span class="text-4xl">🥂</span>
      <h3 class="text-3xl font-bold mt-2">Own a bar? Fill those slow nights.</h3>
      <p class="text-gray-600 text-lg max-w-2xl mx-auto mt-2">We bring pre‑validated crowds that sing (and drink). Venue
        dashboard, real‑time check‑ins & featured spots.</p>
      <a href="#"
        class="inline-block mt-6 bg-gray-900 text-white px-8 py-4 rounded-full font-semibold hover:bg-gray-800 transition">Become
        a partner venue →</a>
    </div>
  </section>
  <CitiesDirectory />
</template>

<script setup lang="ts">
  import { useData } from "vike-vue/useData";
  import CitiesDirectory from "@/components/CitiesDirectory.vue";
  import TextRotate from "@/components/TextRotate.vue";
  import { Data } from "./+data";
  import { EventTypes } from "@/Consts";

  const { city } = useData<Data>();
</script>
